import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * CanonicalMigrationStatus
 * ========================
 * Live dashboard panel for the Canonical Computation Layer rollout.
 * Polls /api/health (public endpoint), reads
 *   .canonical.builders.totals  -> { full, partial, none, all }
 *   .canonical.builders.builders -> { name: status }
 *   .canonical.violations_recent -> int (extent-gate violations)
 *
 * Renders:
 *   • headline progress bar:  "14 / 18  ▓▓▓▓▓▓▓▓▓░░░░░  77.8%"
 *   • per-builder status grid (full=green, partial=amber, none=red)
 *   • violations counter chip
 *
 * Pure visual component; reads only from /api/health and does not
 * mutate state on the backend.
 */
const STATUS_STYLES = {
  full: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  partial: 'bg-amber-100 text-amber-700 border-amber-300',
  none: 'bg-rose-100 text-rose-700 border-rose-300',
  unknown: 'bg-slate-100 text-slate-600 border-slate-300',
};

const STATUS_LABEL = {
  full: 'full',
  partial: 'partial',
  none: 'pending',
  unknown: '?',
};

function makeBlocks(full, total) {
  // 20-block ASCII bar — works in any monospace font, looks clean even
  // in dark mode and shrinks gracefully on mobile.
  const slots = 20;
  const filled = Math.round((full / Math.max(1, total)) * slots);
  return '▓'.repeat(filled) + '░'.repeat(Math.max(0, slots - filled));
}

const CanonicalMigrationStatus = () => {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${backendUrl}/api/health`);
        if (!cancelled) {
          setHealth(res.data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load health');
      }
    };
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [backendUrl]);

  if (error) {
    return (
      <div
        className="bg-white rounded-lg shadow p-6 mb-8"
        data-testid="canonical-migration-status-error"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Canonical Layer — Migration Status
        </h2>
        <p className="text-sm text-rose-600">Unable to fetch /api/health: {error}</p>
      </div>
    );
  }

  if (!health) {
    return (
      <div
        className="bg-white rounded-lg shadow p-6 mb-8"
        data-testid="canonical-migration-status-loading"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Canonical Layer — Migration Status
        </h2>
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  const builders = health?.canonical?.builders;
  const totals = builders?.totals || { full: 0, partial: 0, none: 0, all: 0 };
  const perBuilder = builders?.builders || {};
  const pct = builders?.fully_migrated_pct ?? 0;
  const violations = health?.canonical?.violations_recent ?? 0;
  const gateMode = health?.canonical?.gate_mode || 'strict';

  const sortedBuilders = Object.entries(perBuilder).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  return (
    <div
      className="bg-white rounded-lg shadow p-6 mb-8"
      data-testid="canonical-migration-status"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Canonical Layer — Migration Status
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            All bank-format builders consume from <code>ctx._canonical.*</code>.
            EC honesty + extent lineage are enforced at emit time.
          </p>
        </div>
        <span
          className={`text-xs font-semibold px-2 py-1 rounded border ${
            violations === 0
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-rose-50 text-rose-700 border-rose-200'
          }`}
          data-testid="canonical-violations-chip"
          title="Extent-gate violations in the last ring-buffer window"
        >
          violations: {violations}
        </span>
      </div>

      {/* Headline progress bar */}
      <div
        className="flex items-center gap-4 font-mono text-sm bg-slate-50 border border-slate-200 rounded-md px-4 py-3 mb-4"
        data-testid="canonical-progress-bar"
      >
        <span className="text-slate-700 font-semibold whitespace-nowrap">
          {totals.full} / {totals.all}
        </span>
        <span className="tracking-tighter text-emerald-600 select-none">
          {makeBlocks(totals.full, totals.all)}
        </span>
        <span className="text-slate-700 font-semibold whitespace-nowrap">
          {Number(pct).toFixed(1)}%
        </span>
      </div>

      {/* Aggregate counts */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div
          className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2"
          data-testid="canonical-count-full"
        >
          <div className="text-xs text-emerald-700">Full</div>
          <div className="text-2xl font-bold text-emerald-700">{totals.full}</div>
        </div>
        <div
          className="rounded border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid="canonical-count-partial"
        >
          <div className="text-xs text-amber-700">Partial</div>
          <div className="text-2xl font-bold text-amber-700">{totals.partial}</div>
        </div>
        <div
          className="rounded border border-rose-200 bg-rose-50 px-3 py-2"
          data-testid="canonical-count-pending"
        >
          <div className="text-xs text-rose-700">Pending</div>
          <div className="text-2xl font-bold text-rose-700">{totals.none}</div>
        </div>
      </div>

      {/* Per-builder grid */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
        data-testid="canonical-builder-grid"
      >
        {sortedBuilders.map(([name, status]) => {
          const styleKey = STATUS_STYLES[status] ? status : 'unknown';
          return (
            <div
              key={name}
              className={`flex items-center justify-between border rounded px-2 py-1.5 text-xs ${STATUS_STYLES[styleKey]}`}
              data-testid={`canonical-builder-${name}`}
            >
              <span className="font-mono truncate">{name}</span>
              <span className="ml-2 font-semibold">{STATUS_LABEL[styleKey]}</span>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-xs text-gray-500">
        gate_mode: <span className="font-mono">{gateMode}</span> · refreshed every
        30s · source: <code>/api/health.canonical</code>
      </div>
    </div>
  );
};

export default CanonicalMigrationStatus;
